import React from "react";
import { CAlert, CCard, CCardBody, CSpinner } from "@coreui/react";
import { TradingViewAdvanced } from "@src/components/TradingView/TVAdvanced";
import { preparePropsForDatafeed } from "@src/components/TradingView/utils/tvAdvanced";
import { RunResultProvider } from "@src/components/RunResults/RunResultContext";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import * as virtual from "@src/utils/virtualRun";
import {
  type ExecutionInterval,
  TRACKING_ENDED_LABEL,
  isPositionTrackingEnded,
} from "@src/utils/positionStatus";
import { TrackingEndedFacts } from "@src/components/RunResults/TrackingEndedFacts";
import { Link, useParams, useSearchParams } from "react-router-dom";

export interface Loaded {
  run: T.RunResult;
  title: string;
  subtitle: string;
  interval: ExecutionInterval;
  trackingEnded: boolean;
  /** The execution or backtest this position came out of, when it came from one. */
  parent?: { label: string; href: string };
}

// Only reached when the run a position belongs to cannot be read. The live strategies run at 5m,
// so this is the least wrong guess, and a coarser one hides the bars the trade actually happened on.
const FALLBACK_PERIOD = "5m";

/**
 * The candle size the position was actually traded on.
 *
 * A position does not carry its own timeframe. Its execution or backtest does, under
 * `config.timeframe`, so that is where this reads it from. Charting a 5m strategy on 4h candles
 * shows a trade opening and closing inside a single bar, which is not a chart of anything.
 */
const runContext = async (position: {
  execution_id?: number | null;
  backtest_id?: number | null;
}): Promise<{
  period: string;
  interval: ExecutionInterval;
  parent?: { label: string; href: string };
}> => {
  const kind = position.execution_id ? "execution" : position.backtest_id ? "backtest" : null;
  const runId = position.execution_id ?? position.backtest_id;
  if (!kind || !runId) {
    return { period: FALLBACK_PERIOD, interval: null };
  }
  try {
    const run =
      kind === "execution" ? await tdApi.getExecution(runId) : await tdApi.getBacktest(runId);
    const detail = run as
      | {
          config?: { timeframe?: string };
          name?: string;
          start_ts?: string | null;
          end_ts?: string | null;
        }
      | undefined;
    return {
      period: detail?.config?.timeframe || FALLBACK_PERIOD,
      interval: detail?.start_ts ? { start: detail.start_ts, end: detail.end_ts ?? null } : null,
      parent: {
        label: detail?.name || `${kind} #${runId}`,
        href: `/${kind}/${runId}`,
      },
    };
  } catch {
    // The run is unreadable, but its id is on the position, so the link is still worth offering.
    return {
      period: FALLBACK_PERIOD,
      interval: null,
      parent: { label: `${kind} #${runId}`, href: `/${kind}/${runId}` },
    };
  }
};

/** Trades drawn from a link: everything needed is in the URL, so there is nothing to fetch. */
export const fromLink = (encoded: string): Loaded => {
  const payload = virtual.decodeVirtualRun(encoded);
  const closed = payload.positions.filter((p) => p.out).length;
  const plural = payload.positions.length === 1 ? "" : "s";
  return {
    run: virtual.buildVirtualRunResult(payload),
    interval: null,
    trackingEnded: false,
    title: payload.title || `${payload.symbol} on ${payload.exchange}`,
    subtitle:
      `${payload.positions.length} position${plural} drawn from the link, ${closed} closed. ` +
      "Nothing here is stored: this view is built entirely from the URL, so it reflects whatever " +
      "produced the link rather than any record on the platform.",
  };
};

/** One stored position and its fills, read back from the platform. */
export const fromDatabase = async (id: number): Promise<Loaded> => {
  const position = await tdApi.getPosition(id);
  if (!position) {
    throw new Error(`No position ${id} on the platform.`);
  }

  // Movements carry the entry and exit marks. Without them the chart draws a position with no
  // fills on it, which looks like a rendering bug rather than a position that never traded.
  const [movements, context] = await Promise.all([
    tdApi.getPositionMovements(id, { limit: 100 }),
    runContext(position),
  ]);
  const withMovements = {
    ...position,
    movements: movements?.items ?? [],
  } as unknown as T.AppPosition;

  const symbol = String(position.symbol_name ?? "");
  const exchange = String(position.exchange_name ?? "");
  const open = !position.end_ts;
  return {
    run: virtual.runResultForPositions({
      positions: [withMovements],
      symbol,
      exchange,
      period: context.period,
    }),
    title: `${symbol} · position #${id}`,
    interval: context.interval,
    trackingEnded: isPositionTrackingEnded(position),
    subtitle: isPositionTrackingEnded(position)
      ? `${TRACKING_ENDED_LABEL}: ${position.direction_name ?? ""} on ${exchange}, entered ${String(position.start_ts).slice(0, 10)}.`
      : open
        ? `Open ${position.direction_name ?? ""} on ${exchange}, entered ${String(position.start_ts).slice(0, 10)}.`
        : `Closed ${position.direction_name ?? ""} on ${exchange}, ${String(position.start_ts).slice(0, 10)} to ${String(position.end_ts).slice(0, 10)}.`,
    parent: context.parent,
  };
};

/**
 * A position on a chart, whether the platform stores it or the link carries it.
 *
 * `/position/:id` reads one back from the database. `/position/virtual?d=...` draws trades that
 * were never stored at all, for a screener or a paper book that decides trades without writing
 * them anywhere. Both end up in the same RunResult, so the markers, tooltips and the resolution
 * switcher behave exactly as they do on a real backtest.
 *
 * Candles are fetched live rather than read from the platform's store, which is what lets this
 * work for any contract on any exchange the API can reach.
 */
export const PositionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const encoded = params.get("d");

  const [loaded, setLoaded] = React.useState<Loaded | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    setError(null);
    // Navigating away mid-fetch marks the old effect dead, so its `finally` never clears the flag.
    // Clearing it here means the next route does not inherit a spinner from the one before it.
    setLoading(false);

    if (id) {
      const numeric = Number(id);
      if (!Number.isInteger(numeric) || numeric <= 0) {
        setLoaded(null);
        setError(`"${id}" is not a position id.`);
        return;
      }
      setLoading(true);
      fromDatabase(numeric)
        .then((next) => live && setLoaded(next))
        .catch((e: unknown) => {
          if (!live) return;
          setLoaded(null);
          setError(e instanceof Error ? e.message : "Could not load that position.");
        })
        .finally(() => live && setLoading(false));
      return () => {
        live = false;
      };
    }

    if (!encoded) {
      setLoaded(null);
      setError("No position in the link. Expected /position/<id>, or ?d= with an encoded one.");
      return;
    }
    try {
      setLoaded(fromLink(encoded));
    } catch (e) {
      setLoaded(null);
      setError(e instanceof Error ? e.message : "Could not read the link.");
    }
    return () => {
      live = false;
    };
  }, [id, encoded]);

  if (loading) {
    return (
      <div className="PositionPage PositionPage-loading text-center py-5">
        <CSpinner />
      </div>
    );
  }

  if (error || !loaded) {
    return (
      <div className="PositionPage">
        <CAlert color="danger" className="PositionPage-error">
          {error}
        </CAlert>
      </div>
    );
  }

  return (
    <div className="PositionPage">
      <div className="PositionPage-header mb-2">
        <h5 className="mb-1">{loaded.title}</h5>
        <p className="text-muted mb-0 small">
          {loaded.subtitle}
          {loaded.parent && (
            <>
              {" Part of "}
              <Link className="PositionPage-parentLink" to={loaded.parent.href}>
                {loaded.parent.label}
              </Link>
              .
            </>
          )}
        </p>
      </div>

      {loaded.trackingEnded && loaded.run.positions?.[0] && (
        <TrackingEndedFacts
          position={loaded.run.positions[0]}
          executionInterval={loaded.interval}
        />
      )}
      <CCard className="PositionPage-chartCard">
        <CCardBody className="p-0">
          <div className="PositionPage-chart" style={{ height: "620px", width: "100%" }}>
            {/* No run backs this chart, so the provider is given nulls. The chart only needs the
                props below; the context exists for resolution changes and the candle-source note. */}
            <RunResultProvider>
              {/* One position is read forwards from its entry. A whole run is not, so this stays
                  off everywhere else. Tracking-ended positions are dropped before framing. */}
              <TradingViewAdvanced
                {...preparePropsForDatafeed(loaded.run, true, { openOnEntry: true })}
              />
            </RunResultProvider>
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};
